/**
 * Profile — Redesigned profile page with role-based tabs.
 *
 * Tabs (role-dependent):
 * - Profil: 2-column layout (personal data + password/appearance)
 * - Signatur: Unified signature with usage toggles
 * - Vertretungen: Deputy management
 * - Benachrichtigungen: Notification preferences
 * - OMS-Verbindung: OMS credentials + sync
 *
 * @page /profil
 * @roles alle (authentifiziert)
 * @i18n profile, vertretung, omsSettings, common
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ToggleSwitch } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';

const NOTIF_CATEGORIES = [
 { key: 'forecast', label: 'Forecast' },
 { key: 'schicht', label: 'Schichtplanung' },
 { key: 'abwesenheit', label: 'Abwesenheit' },
 { key: 'system', label: 'System' },
 { key: 'user', label: 'User' },
];

function getProfileTabs(rolle, permissions, t) {
 const tabs = [{ id: 'profil', label: t('profile.profile') }];

 const hasSignatur = ['administrator', 'ops_manager', 'customer_success', 'controlling', 'teamleiter'].includes(rolle);
 // #1270 — „Meine Vertretung" Self-Service für ALLE internen MA (Portal-Accounts ausgeschlossen).
 const hasVertretung = !!rolle && !['portal_kunde', 'portal_mitarbeiter'].includes(rolle);
 // #1426-Follow-up — OMS-Credentials braucht jeder, der Cases beantwortet, unabhängig von der Rolle.
 const hasOMS = ['administrator', 'customer_success'].includes(rolle)
  || (!!permissions?.cases && permissions.cases !== 'none');

 if (hasSignatur) tabs.push({ id: 'signatur', label: t('profile.signature') });
 if (hasVertretung) tabs.push({ id: 'vertretungen', label: t('vertretung.title') });
 tabs.push({ id: 'benachrichtigungen', label: t('profile.notifications') });
 if (hasOMS) tabs.push({ id: 'oms', label: t('profile.omsConnection') });

 return tabs;
}

export default function Profile() {
 const { user, updateProfile, checkAuth } = useAuth();
 const { t, language } = useLanguage();
 const [tab, setTab] = useTabParam('profil');

 // Profile form
 const [form, setForm] = useState({ vorname: user?.vorname || '', nachname: user?.nachname || '', email: user?.email || '', calendly_url: user?.calendly_url || '', signatur_grussformel: user?.signatur_grussformel || '' });
 const [msg, setMsg] = useState('');
 const [err, setErr] = useState('');
 const [saving, setSaving] = useState(false);

 // Password
 const [pwForm, setPwForm] = useState({ current_password: '', password: '', password2: '' });
 const [pwMsg, setPwMsg] = useState('');
 const [pwErr, setPwErr] = useState('');
 const [savingPw, setSavingPw] = useState(false);

 // Avatar
 const [avatarUrl, setAvatarUrl] = useState(null);
 const [avatarUploading, setAvatarUploading] = useState(false);
 const [avatarErr, setAvatarErr] = useState('');
 const fileRef = useRef(null);

 // Signature (unified)
 const [sigHtml, setSigHtml] = useState('');
 const [sigUseCases, setSigUseCases] = useState(false);
 const [sigUseSales, setSigUseSales] = useState(false);
 const [sigUseAdress, setSigUseAdress] = useState(false);
 const [sigLoading, setSigLoading] = useState(false);
 const [sigSaving, setSigSaving] = useState(false);
 const [sigMsg, setSigMsg] = useState('');
 const [sigErr, setSigErr] = useState('');

 // Notifications
 const [prefs, setPrefs] = useState([]);
 const [prefsLoading, setPrefsLoading] = useState(false);
 const [prefsSaving, setPrefsSaving] = useState(null);
 // Granulare Event-View (#898)
 const [notifView, setNotifView] = useState('kategorie'); // 'kategorie' | 'event'
 const [events, setEvents] = useState([]);
 const [eventsLoading, setEventsLoading] = useState(false);
 const [eventSaving, setEventSaving] = useState(null);
 const [eventFilter, setEventFilter] = useState('');
 // #1049 Quiet-Hours (Per-User)
 const [qh, setQh] = useState({ enabled: false, start_time: '20:00', end_time: '08:00', channels: ['sms','telegram'], weekend_only: false });
 const [qhLoading, setQhLoading] = useState(false);
 const [qhSaving, setQhSaving] = useState(false);
 const [qhMsg, setQhMsg] = useState('');

 // #1422 Softphone-Toggle (users.softphone_enabled)
 const [spEnabled, setSpEnabled] = useState(!!user?.softphone_enabled);
 const [spSaving, setSpSaving] = useState(false);
 // #1425 Durchstell-Kaskade (pro-MA-Konfig)
 const [kk, setKk] = useState({ aktiv: false, softphone_timeout_sek: 15, handy_nummer: '', handy_timeout_sek: 20, vertretung_user_id: null, handy_vorschlag: '' });
 const [kkTargets, setKkTargets] = useState([]);
 const [kkSaving, setKkSaving] = useState(false);
 const [kkMsg, setKkMsg] = useState('');
 const [kkErr, setKkErr] = useState('');

 // OMS
 const [omsForm, setOmsForm] = useState({ oms_username: '', oms_password: '' });
 const [omsSettings, setOmsSettings] = useState(null);
 const [omsLoading, setOmsLoading] = useState(false);
 const [omsSaving, setOmsSaving] = useState(false);
 const [omsTesting, setOmsTesting] = useState(false);
 const [omsMsg, setOmsMsg] = useState('');
 const [omsErr, setOmsErr] = useState('');
 const [syncStatus, setSyncStatus] = useState(null);
 const [syncing, setSyncing] = useState(false);
 const [syncMsg, setSyncMsg] = useState('');
 const [syncErr, setSyncErr] = useState('');
 const [omsUsers, setOmsUsers] = useState([]);
 const [omsUsersLoading, setOmsUsersLoading] = useState(false);

 // #1485 Calendly (Telefon-Terminbuchung) — API-Key sensibel, wird nie zurückgegeben.
 const [calForm, setCalForm] = useState({ calendly_api_key: '', calendly_event_type_uri: '' });
 const [calSettings, setCalSettings] = useState(null);
 const [calSaving, setCalSaving] = useState(false);
 const [calTesting, setCalTesting] = useState(false);
 const [calMsg, setCalMsg] = useState('');
 const [calErr, setCalErr] = useState('');
 // #1485 Nachzug — Event-Type-Dropdown statt manueller URI.
 const [calEventTypes, setCalEventTypes] = useState(null);
 const [calEtLoading, setCalEtLoading] = useState(false);
 const [calManualUri, setCalManualUri] = useState(false);

 // Vertretungen
 const [vtList, setVtList] = useState([]);
 const [vtUsers, setVtUsers] = useState([]);
 const [vtLoading, setVtLoading] = useState(false);
 const [vtModal, setVtModal] = useState(false);
 const [vtForm, setVtForm] = useState({ user_id: '', vertreter_id: '', permanent: true, gueltig_von: '', gueltig_bis: '', auto_vertretung: false });
 const [vtSaving, setVtSaving] = useState(false);
 const [vtMsg, setVtMsg] = useState('');
 const [vtErr, setVtErr] = useState('');

 const isAdmin = ['administrator', 'ops_manager'].includes(user?.rolle);
 const tabs = user ? getProfileTabs(user.rolle, user.permissions, t) : [];

 // ─── Avatar ───
 const loadAvatar = useCallback(() => {
 if (!user) return;
 setAvatarUrl(`${API}/api/users/${user.id}/avatar?t=${Date.now()}`);
 }, [user]);

 useEffect(() => { loadAvatar(); }, [loadAvatar]);

 // ─── Tab data loading ───
 useEffect(() => {
 if (tab === 'signatur') loadSignature();
 if (tab === 'benachrichtigungen') {
 setPrefsLoading(true);
 fetch(`${API}/api/notifications/preferences`, { credentials: 'include' })
 .then(r => r.json()).then(setPrefs).catch(() => {}).finally(() => setPrefsLoading(false));
 setEventsLoading(true);
 fetch(`${API}/api/notifications/events`, { credentials: 'include' })
 .then(r => r.json()).then(d => setEvents(d.events || [])).catch(() => {}).finally(() => setEventsLoading(false));
 setQhLoading(true);
 fetch(`${API}/api/profile/quiet-hours`, { credentials: 'include' })
 .then(r => r.json()).then(d => {
 if (d && typeof d === 'object') setQh(prev => ({ ...prev, ...d, channels: Array.isArray(d.channels) ? d.channels : prev.channels }));
 }).catch(() => {}).finally(() => setQhLoading(false));
 }
 if (tab === 'oms') { loadOmsSettings(); loadSyncStatus(); loadOmsUsers(); }
 if (tab === 'vertretungen') loadVertretungen();
 // #1425: Kaskaden-Konfig + Ziel-Liste (Softphone-Card im Profil-Tab)
 if (tab === 'profil' && !['portal_kunde', 'portal_mitarbeiter', 'jv_partner'].includes(user?.rolle)) {
 fetch(`${API}/api/twilio/voice/settings`, { credentials: 'include' })
 .then(r => r.json()).then(d => { if (d?.kaskade) setKk(k => ({ ...k, ...d.kaskade })); }).catch(() => {});
 fetch(`${API}/api/twilio/voice/cascade-targets?exclude_self=1`, { credentials: 'include' })
 .then(r => r.json()).then(d => setKkTargets(d.targets || [])).catch(() => {});
 loadCalSettings(); // #1485 Calendly-Terminbuchung
 }
 }, [tab]);

 // ─── Loaders ───
 const loadSignature = async () => {
 setSigLoading(true);
 try {
 const res = await fetch(`${API}/api/users/me/signatur`, { credentials: 'include' });
 if (res.ok) {
 const d = await res.json();
 setSigHtml(d.signatur_html || '');
 setSigUseCases(d.signatur_use_cases || false);
 setSigUseSales(d.signatur_use_sales || false);
 setSigUseAdress(d.signatur_use_adressklaerung || false);
 }
 } catch { /* ignore */ }
 finally { setSigLoading(false); }
 };

 const loadOmsSettings = async () => {
 setOmsLoading(true);
 try {
 const res = await fetch(`${API}/api/users/me/oms-settings`, { credentials: 'include' });
 const data = await res.json();
 setOmsSettings(data);
 if (data.oms_username) setOmsForm(f => ({ ...f, oms_username: data.oms_username }));
 } catch { /* ignore */ }
 finally { setOmsLoading(false); }
 };

 const loadSyncStatus = async () => {
 try {
 const res = await fetch(`${API}/api/users/me/profile-sync-status`, { credentials: 'include' });
 if (res.ok) setSyncStatus(await res.json());
 } catch { /* ignore */ }
 };

 // #1485 Calendly-Settings (ohne API-Key — der wird nie zurückgegeben).
 const loadCalSettings = async () => {
 try {
 const res = await fetch(`${API}/api/users/me/calendly-settings`, { credentials: 'include' });
 const data = await res.json();
 setCalSettings(data);
 setCalForm(f => ({ ...f, calendly_event_type_uri: data.calendly_event_type_uri || '' }));
 } catch { /* ignore */ }
 };

 const saveCalSettings = async () => {
 setCalMsg(''); setCalErr(''); setCalSaving(true);
 try {
 const res = await fetch(`${API}/api/users/me/calendly-settings`, {
 method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ calendly_api_key: calForm.calendly_api_key, calendly_event_type_uri: calForm.calendly_event_type_uri }),
 });
 const data = await res.json();
 if (data.ok) { setCalMsg(data.message || 'Gespeichert.'); setCalForm(f => ({ ...f, calendly_api_key: '' })); await loadCalSettings(); }
 else setCalErr(data.error || 'Speichern fehlgeschlagen.');
 } catch (e) { setCalErr(e.message); }
 finally { setCalSaving(false); }
 };

 const testCalSettings = async () => {
 setCalMsg(''); setCalErr(''); setCalTesting(true);
 try {
 const res = await fetch(`${API}/api/users/me/calendly-settings/test`, { method: 'POST', credentials: 'include' });
 const data = await res.json();
 if (data.verified) { setCalMsg(data.message || 'Verbindung erfolgreich.'); await loadCalSettings(); }
 else setCalErr(data.error || 'Verbindung fehlgeschlagen.');
 } catch (e) { setCalErr(e.message); }
 finally { setCalTesting(false); }
 };

 // #1485 Nachzug — Event-Types via Calendly-API laden (Dropdown). PAT frisch
 // aus dem Formular (falls eingegeben) ODER der gespeicherte aus der DB.
 const loadCalEventTypes = async () => {
 setCalErr(''); setCalEtLoading(true);
 try {
 const res = await fetch(`${API}/api/users/me/calendly-event-types`, {
 method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ api_key: calForm.calendly_api_key || undefined }),
 });
 const data = await res.json();
 if (data.ok) {
 setCalEventTypes(data.event_types || []);
 if (!data.event_types?.length) setCalErr('Keine aktiven Event-Types im Calendly-Account gefunden.');
 } else { setCalEventTypes(null); setCalErr(data.error || 'Event-Types konnten nicht geladen werden.'); }
 } catch (e) { setCalEventTypes(null); setCalErr(e.message); }
 finally { setCalEtLoading(false); }
 };

 const loadOmsUsers = async () => {
 if (!isAdmin) return;
 setOmsUsersLoading(true);
 try {
 const res = await fetch(`${API}/api/auth/oms-users`, { credentials: 'include' });
 if (res.ok) {
 const data = await res.json();
 setOmsUsers(data.users || []);
 }
 } catch { /* ignore */ }
 finally { setOmsUsersLoading(false); }
 };

 const handleOmsUserSelect = async (omsUserId) => {
 setOmsMsg(''); setOmsErr('');
 try {
 const res = await fetch(`${API}/api/users/${user.id}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ oms_user_id: omsUserId ? Number(omsUserId) : null }),
 });
 if (!res.ok) throw new Error('Fehler beim Speichern');
 setOmsMsg(t('common.saved'));
 loadOmsSettings();
 } catch (err) { setOmsErr(err.message); }
 };

 const loadVertretungen = async () => {
 setVtLoading(true);
 try {
 const [vtRes, usersRes] = await Promise.all([
 fetch(`${API}/api/cases/vertretungen`, { credentials: 'include' }).then(r => r.json()),
 fetch(`${API}/api/cases/vertretungen/users`, { credentials: 'include' }).then(r => r.json()),
 ]);
 setVtList(vtRes.vertretungen || []);
 setVtUsers(usersRes.users || []);
 } catch { /* ignore */ }
 finally { setVtLoading(false); }
 };

 // ─── Handlers ───
 const handleProfile = async (e) => {
 e.preventDefault();
 setErr(''); setMsg(''); setSaving(true);
 try {
 await updateProfile({ vorname: form.vorname, nachname: form.nachname, email: form.email, calendly_url: form.calendly_url, signatur_grussformel: form.signatur_grussformel });
 setMsg(t('profile.saved'));
 } catch (err) { setErr(err.message); }
 finally { setSaving(false); }
 };

 const handlePassword = async (e) => {
 e.preventDefault();
 setPwErr(''); setPwMsg('');
 if (pwForm.password !== pwForm.password2) { setPwErr(t('auth.passwordsMismatch')); return; }
 if (pwForm.password.length < 8) { setPwErr(t('auth.passwordMinLength')); return; }
 setSavingPw(true);
 try {
 await updateProfile({ current_password: pwForm.current_password, password: pwForm.password });
 setPwMsg(t('profile.passwordChanged'));
 setPwForm({ current_password: '', password: '', password2: '' });
 } catch (err) { setPwErr(err.message); }
 finally { setSavingPw(false); }
 };

 const handleAvatarUpload = async (file) => {
 if (!file) return;
 if (!file.type.startsWith('image/')) { setAvatarErr(t('profile.avatarInvalidType')); return; }
 if (file.size > 5 * 1024 * 1024) { setAvatarErr(t('profile.avatarMaxSize')); return; }
 setAvatarErr(''); setAvatarUploading(true);
 try {
 const formData = new FormData();
 formData.append('avatar', file);
 const res = await fetch(`${API}/api/users/me/avatar`, { method: 'POST', credentials: 'include', body: formData });
 if (res.ok) loadAvatar();
 else { const d = await res.json().catch(() => ({})); setAvatarErr(d.error || 'Upload failed'); }
 } catch (err) { setAvatarErr(err.message); }
 finally { setAvatarUploading(false); }
 };

 const handleAvatarDelete = async () => {
 setAvatarErr(''); setAvatarUploading(true);
 try { await fetch(`${API}/api/users/me/avatar`, { method: 'DELETE', credentials: 'include' }); setAvatarUrl(null); }
 catch (err) { setAvatarErr(err.message); }
 finally { setAvatarUploading(false); }
 };

 // #1059 Punkt 11 — Foto aus Mitarbeiter-Datensatz uebernehmen
 const handleAvatarFromMa = async () => {
 setAvatarErr(''); setAvatarUploading(true);
 try {
 const res = await fetch(`${API}/api/users/me/avatar/from-mitarbeiter`, { method: 'POST', credentials: 'include' });
 if (res.ok) loadAvatar();
 else { const d = await res.json().catch(() => ({})); setAvatarErr(d.error || 'Sync failed'); }
 } catch (err) { setAvatarErr(err.message); }
 finally { setAvatarUploading(false); }
 };

 const handleSignatureSave = async () => {
 setSigErr(''); setSigMsg(''); setSigSaving(true);
 try {
 const res = await fetch(`${API}/api/users/me/signatur`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ signatur_html: sigHtml, signatur_use_cases: sigUseCases, signatur_use_sales: sigUseSales, signatur_use_adressklaerung: sigUseAdress }),
 });
 if (res.ok) setSigMsg(t('profile.signatureSaved'));
 else { const d = await res.json().catch(() => ({})); setSigErr(d.error || 'Error'); }
 } catch (err) { setSigErr(err.message); }
 finally { setSigSaving(false); }
 };

 const handleOmsSave = async (e) => {
 e.preventDefault();
 setOmsErr(''); setOmsMsg('');
 if (!omsForm.oms_username?.trim() || !omsForm.oms_password?.trim()) { setOmsErr(t('omsSettings.username') + ' + ' + t('omsSettings.password') + ' required'); return; }
 setOmsSaving(true);
 try {
 const res = await fetch(`${API}/api/users/me/oms-settings`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ oms_username: omsForm.oms_username, oms_password: omsForm.oms_password }),
 });
 const data = await res.json();
 if (data.ok) { setOmsMsg(t('omsSettings.savedMessage')); setOmsForm(f => ({ ...f, oms_password: '' })); await loadOmsSettings(); }
 else setOmsErr(data.error || 'Error');
 } catch (err) { setOmsErr(err.message); }
 finally { setOmsSaving(false); }
 };

 const handleOmsTest = async () => {
 setOmsErr(''); setOmsMsg(''); setOmsTesting(true);
 try {
 const res = await fetch(`${API}/api/users/me/oms-settings/test`, { method: 'POST', credentials: 'include' });
 const data = await res.json();
 if (data.ok && data.verified) { setOmsMsg(t('omsSettings.verifiedMessage', { id: data.oms_user_id })); }
 else { setOmsErr(data.error || t('omsSettings.failedMessage')); }
 await loadOmsSettings();
 } catch (err) { setOmsErr(err.message); }
 finally { setOmsTesting(false); }
 };

 const handleOmsSync = async () => {
 setSyncErr(''); setSyncMsg(''); setSyncing(true);
 try {
 const res = await fetch(`${API}/api/users/me/oms-sync-profile`, { method: 'POST', credentials: 'include' });
 const data = await res.json();
 if (data.ok) { setSyncMsg(t('profile.omsSyncSuccess')); await loadSyncStatus(); }
 else setSyncErr(data.error || t('profile.omsSyncFailed'));
 } catch (err) { setSyncErr(err.message); }
 finally { setSyncing(false); }
 };

 const handleVtSave = async (e) => {
 e?.preventDefault();
 setVtSaving(true); setVtMsg(''); setVtErr('');
 try {
 const body = {
 user_id: Number(vtForm.user_id) || user.id,
 vertreter_id: Number(vtForm.vertreter_id),
 permanent: vtForm.permanent,
 auto_vertretung: !!vtForm.auto_vertretung,
 ...(vtForm.permanent ? {} : { gueltig_von: vtForm.gueltig_von, gueltig_bis: vtForm.gueltig_bis }),
 };
 const res = await fetch(`${API}/api/cases/vertretungen`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(body),
 });
 if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Error'); }
 setVtMsg(t('common.saved'));
 setVtModal(false);
 setVtForm({ user_id: '', vertreter_id: '', permanent: true, gueltig_von: '', gueltig_bis: '', auto_vertretung: false });
 loadVertretungen();
 } catch (e) { setVtErr(e.message); }
 finally { setVtSaving(false); }
 };

 const handleVtDelete = async (id) => {
 try { await fetch(`${API}/api/cases/vertretungen/${id}`, { method: 'DELETE', credentials: 'include' }); loadVertretungen(); }
 catch { /* ignore */ }
 };

 // #1270 — Auto-Vertretung pro Zeile umschalten (PUT, optimistisch + Reload bei Fehler).
 const handleVtToggleAuto = async (v, value) => {
 setVtErr('');
 setVtList(list => list.map(x => x.id === v.id ? { ...x, auto_vertretung: value } : x));
 try {
 const res = await fetch(`${API}/api/cases/vertretungen/${v.id}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ auto_vertretung: value }),
 });
 if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Error'); }
 } catch (e) { setVtErr(e.message); loadVertretungen(); }
 };

 const updatePref = async (kategorie, field, value) => {
 const current = prefs.find(p => p.kategorie === kategorie);
 if (!current) return;
 const updated = { ...current, [field]: value };
 setPrefs(prefs.map(p => p.kategorie === kategorie ? updated : p));
 setPrefsSaving(kategorie);
 try {
 await fetch(`${API}/api/notifications/preferences`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ kategorie, in_app: updated.in_app, email: updated.email, digest: updated.digest }),
 });
 } catch { /* ignore */ }
 finally { setPrefsSaving(null); }
 };

 // Granulare Pref je Event (#898)
 const updateEventPref = async (eventKey, field, value) => {
 const ev = events.find(e => e.event_key === eventKey);
 if (!ev) return;
 const nextEff = { ...ev.effective, [field]: value };
 setEvents(events.map(e => e.event_key === eventKey ? { ...e, effective: nextEff, user_pref: { ...(e.user_pref || {}), ...nextEff } } : e));
 setEventSaving(eventKey);
 try {
 await fetch(`${API}/api/notifications/events/${encodeURIComponent(eventKey)}/preference`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(nextEff),
 });
 } catch { /* ignore */ }
 finally { setEventSaving(null); }
 };

 const resetEventPref = async (eventKey) => {
 setEventSaving(eventKey);
 try {
 await fetch(`${API}/api/notifications/events/${encodeURIComponent(eventKey)}/preference`, {
 method: 'DELETE', credentials: 'include',
 });
 const { events: fresh } = await fetch(`${API}/api/notifications/events`, { credentials: 'include' }).then(r => r.json());
 setEvents(fresh || []);
 } catch { /* ignore */ }
 finally { setEventSaving(null); }
 };

 // #1049 Quiet-Hours
 const saveQuietHours = async () => {
 setQhSaving(true); setQhMsg('');
 try {
 const res = await fetch(`${API}/api/profile/quiet-hours`, {
 method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({
 enabled: !!qh.enabled,
 start_time: qh.start_time, end_time: qh.end_time,
 channels: qh.channels, weekend_only: !!qh.weekend_only,
 }),
 });
 if (!res.ok) throw new Error('save_failed');
 setQhMsg(t('common.saved'));
 setTimeout(() => setQhMsg(''), 2500);
 } catch { setQhMsg(t('rolesAdmin.saveFailed')); }
 finally { setQhSaving(false); }
 };
 const toggleQhChannel = (ch) => {
 setQh(prev => ({
 ...prev,
 channels: prev.channels.includes(ch) ? prev.channels.filter(c => c !== ch) : [...prev.channels, ch],
 }));
 };

 if (!user) return null;

 const initials = `${user.vorname?.[0] || ''}${user.nachname?.[0] || ''}`.toUpperCase();
 const inp = "w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-xl px-3 py-2.5 text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc] focus:ring-2 focus:ring-[#338edc]/10 transition-colors";
 const label = "block text-xs font-medium text-[var(--text-secondary)] mb-1.5";
 const card = "bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-6";
 const btn = "bg-[#338edc] hover:bg-[#2b7bc4] disabled:opacity-50 text-white text-xs font-semibold px-5 py-2.5 rounded-[10px] transition-colors";
 const btnSecondary = "border border-[var(--border-input)] hover:border-[var(--border-input-focus)] text-[var(--text-primary)] text-xs font-medium px-4 py-2 rounded-[10px] transition-colors";

 const rolleLabel = t(`topbar.role.${user.rolle}`) !== `topbar.role.${user.rolle}` ? t(`topbar.role.${user.rolle}`) : user.rolle;

 const AvatarDisplay = ({ size = 64 }) => {
 const [imgError, setImgError] = useState(false);
 const s = size === 64 ? 'w-16 h-16' : 'w-20 h-20';
 const ts = size === 64 ? 'text-lg' : 'text-xl';
 if (avatarUrl && !imgError) {
 return <img src={avatarUrl} alt={`${user.vorname} ${user.nachname}`} className={`${s} rounded-full object-cover`} onError={() => setImgError(true)} />;
 }
 return (
 <div className={`${s} rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center`}>
 <span className={`${ts} font-bold text-white`}>{initials}</span>
 </div>
 );
 };

 const omsStatus = !omsSettings?.oms_username
 ? { color: 'bg-[var(--text-muted,#9CA3AF)]', text: t('omsSettings.statusNotConfigured') }
 : omsSettings.oms_connection_verified
 ? { color: 'bg-emerald-500', text: t('omsSettings.statusVerified', { id: omsSettings.oms_user_id }) }
 : { color: 'bg-amber-500', text: t('omsSettings.statusPending') };

 return (
 <div className="p-6 space-y-6">
 <h1 className="text-lg font-bold text-[var(--text-primary)]">{t('profile.title')}</h1>

 {/* Tabs */}
 <div className="flex gap-1 bg-[var(--bg-inset)] p-1 rounded-xl w-fit">
 {tabs.map(tb => (
 <button key={tb.id} onClick={() => setTab(tb.id)}
 className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tab === tb.id
 ? 'bg-[var(--bg-card)] shadow-sm text-[var(--text-primary)] font-semibold'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
 {tb.label}
 </button>
 ))}
 </div>

 {/* ═══ PROFIL TAB ═══ */}
 {tab === 'profil' && (
 <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
 {/* Left: Personal Data (3/5) */}
 <div className={`${card} lg:col-span-3`}>
 {/* Avatar + Name header */}
 <div className="flex items-start gap-4 mb-6">
 <div className="flex flex-col items-center gap-2">
 <div className="relative group cursor-pointer"
 onClick={() => fileRef.current?.click()}
 onDragOver={(e) => e.preventDefault()}
 onDrop={(e) => { e.preventDefault(); handleAvatarUpload(e.dataTransfer?.files?.[0]); }}>
 <AvatarDisplay size={64} />
 <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
 {avatarUploading ? (
 <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
 ) : (
 <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
 <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
 <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
 </svg>
 )}
 </div>
 <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
 onChange={(e) => handleAvatarUpload(e.target.files?.[0])} />
 </div>
 {avatarUrl && (
 <button onClick={(e) => { e.stopPropagation(); handleAvatarDelete(); }}
 className="text-[10px] text-red-400 hover:text-red-300 transition-colors">
 {t('profile.avatarRemove')}
 </button>
 )}
 {/* #1059 Punkt 11 — Sync from MA-record */}
 {user.mitarbeiter_id && (
 <button onClick={(e) => { e.stopPropagation(); handleAvatarFromMa(); }}
 disabled={avatarUploading}
 className="text-[10px] text-[#338edc] hover:text-[#2b7bc4] transition-colors disabled:opacity-50">
 {t('profile.avatarFromMa')}
 </button>
 )}
 </div>
 <div className="flex-1">
 <div className="text-base font-semibold text-[var(--text-primary)]">{user.vorname} {user.nachname}</div>
 <div className="text-sm text-[var(--text-secondary)]">{user.email}</div>
 <span className="inline-block mt-1 px-2 py-0.5 bg-primary-500/10 text-primary-500 text-[10px] font-medium rounded-full">{rolleLabel}</span>
 {user.avatar_synced_from_ma && (
 <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 bg-[#338edc]/10 text-[#338edc] text-[10px] font-medium rounded-full">
 {t('profile.avatarSourceMa') || 'Quelle: Mitarbeiter-Foto'}
 </div>
 )}
 {avatarErr && <div className="mt-2 text-[10px] text-red-400">{avatarErr}</div>}
 <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">{t('profile.avatarHint')}</p>
 </div>
 </div>

 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('profile.personalData')}</h3>
 <form onSubmit={handleProfile} className="space-y-3">
 {msg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500">{msg}</div>}
 {err && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">{err}</div>}
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className={label}>{t('profile.firstName')}</label>
 <input type="text" value={form.vorname} onChange={e => setForm(f => ({ ...f, vorname: e.target.value }))} className={inp} required />
 </div>
 <div>
 <label className={label}>{t('profile.lastName')}</label>
 <input type="text" value={form.nachname} onChange={e => setForm(f => ({ ...f, nachname: e.target.value }))} className={inp} required />
 </div>
 </div>
 <div>
 <label className={label}>{t('profile.email')}</label>
 <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={inp} required />
 </div>
 {!['kunde', 'portal_kunde'].includes(user?.rolle) && (
 <>
 <div>
 <label className={label}>{t('profile.calendlyUrl')}</label>
 <input type="url" value={form.calendly_url} onChange={e => setForm(f => ({ ...f, calendly_url: e.target.value }))}
 className={inp} placeholder="https://calendly.com/your-link" />
 <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">{t('profile.calendlyHint')}</p>
 </div>
 <div>
 <label className={label}>{t('profile.signatureGreeting') || 'Persönliche Grußformel'}</label>
 <input type="text" value={form.signatur_grussformel} onChange={e => setForm(f => ({ ...f, signatur_grussformel: e.target.value }))}
 className={inp} placeholder={t('profile.signatureGreetingPlaceholder') || 'z.B. Beste Grüße / Liebe Grüße / Mit freundlichen Grüßen'} />
 <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">{t('profile.signatureGreetingHint') || 'Wird in Sales-Workflow-Mails verwendet, wenn Signatur-Modus „Persönlich" gewählt ist.'}</p>
 </div>
 </>
 )}
 <div className="pt-1">
 <button type="submit" disabled={saving} className={btn}>
 {saving ? t('common.saving') : t('profile.saveProfile')}
 </button>
 </div>
 </form>

 {/* #1485 Calendly Terminbuchung (Telefon-Hotline) — eigener Speicher-Flow, API-Key sensibel */}
 {!['kunde', 'portal_kunde', 'portal_mitarbeiter', 'jv_partner'].includes(user?.rolle) && (
 <div className="mt-6 pt-6 border-t border-[var(--border,#E5E7EB)] space-y-3">
 <div>
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">Telefon-Terminbuchung (Calendly)</h3>
 <p className="mt-1 text-[10px] text-[var(--text-tertiary)]">Hinterlege deinen Calendly Personal Access Token + die Event-Type-URI, damit die Telefon-Hotline Termine in deinem Kalender vorlesen und buchen kann. Der Token wird verschlüsselt gespeichert und nie angezeigt.</p>
 </div>
 {calMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500">{calMsg}</div>}
 {calErr && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">{calErr}</div>}
 <div>
 <label className={label}>Calendly API-Key (Personal Access Token)</label>
 <input type="password" value={calForm.calendly_api_key} autoComplete="off"
 onChange={e => setCalForm(f => ({ ...f, calendly_api_key: e.target.value }))}
 className={inp} placeholder={calSettings?.has_key ? '•••••••• (gespeichert — leer lassen zum Beibehalten)' : 'eyJ… Token aus calendly.com → Integrations'} />
 </div>
 <div>
 <div className="flex items-center justify-between">
 <label className={label}>Termin-Typ</label>
 <button type="button" onClick={loadCalEventTypes} disabled={calEtLoading || (!calForm.calendly_api_key && !calSettings?.has_key)}
 className="text-[11px] text-[#338edc] hover:underline disabled:opacity-50 disabled:no-underline">
 {calEtLoading ? 'Lade…' : 'Termin-Typen laden'}
 </button>
 </div>
 {Array.isArray(calEventTypes) && calEventTypes.length > 0 ? (
 <select value={calForm.calendly_event_type_uri}
 onChange={e => setCalForm(f => ({ ...f, calendly_event_type_uri: e.target.value }))}
 className={inp}>
 <option value="">— Termin-Typ wählen —</option>
 {calEventTypes.map(et => (
 <option key={et.uri} value={et.uri}>{et.name}{et.duration ? ` (${et.duration} Min)` : ''}</option>
 ))}
 </select>
 ) : (
 <p className="text-[11px] text-[var(--text-tertiary)] py-1">{calSettings?.has_key || calForm.calendly_api_key ? 'Klicke „Termin-Typen laden", um deine Calendly-Termin-Typen als Auswahl anzuzeigen.' : 'Zuerst API-Key eingeben und speichern, dann Termin-Typen laden.'}</p>
 )}
 <button type="button" onClick={() => setCalManualUri(v => !v)} className="mt-1 text-[10px] text-[var(--text-tertiary)] hover:underline">
 {calManualUri ? 'Manuelle URI ausblenden' : 'URI manuell eingeben'}
 </button>
 {calManualUri && (
 <input type="url" value={calForm.calendly_event_type_uri}
 onChange={e => setCalForm(f => ({ ...f, calendly_event_type_uri: e.target.value }))}
 className={`${inp} mt-1`} placeholder="https://api.calendly.com/event_types/AAAAAAAAAAAA" />
 )}
 </div>
 <div className="flex items-center gap-2">
 <button type="button" disabled={calSaving} onClick={saveCalSettings} className={btnSecondary}>
 {calSaving ? t('common.saving') : t('common.save') || 'Speichern'}
 </button>
 <button type="button" disabled={calTesting || !calSettings?.has_key} onClick={testCalSettings} className={btnSecondary}>
 {calTesting ? 'Teste…' : 'Verbindung testen'}
 </button>
 {calSettings?.calendly_connection_verified && (
 <span className="text-xs text-emerald-500">✓ Verbunden</span>
 )}
 </div>
 </div>
 )}
 </div>

 {/* Right column (2/5) */}
 <div className="lg:col-span-2 space-y-6">
 {/* Password */}
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('profile.changePassword')}</h3>
 <form onSubmit={handlePassword} className="space-y-3">
 {pwMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500">{pwMsg}</div>}
 {pwErr && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">{pwErr}</div>}
 <div>
 <label className={label}>{t('profile.currentPassword')}</label>
 <input type="password" value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))}
 className={inp} required autoComplete="current-password" />
 </div>
 <div>
 <label className={label}>{t('profile.newPassword')}</label>
 <input type="password" value={pwForm.password} onChange={e => setPwForm(f => ({ ...f, password: e.target.value }))}
 className={inp} placeholder={t('auth.minChars')} required autoComplete="new-password" />
 </div>
 <div>
 <label className={label}>{t('profile.confirmNewPassword')}</label>
 <input type="password" value={pwForm.password2} onChange={e => setPwForm(f => ({ ...f, password2: e.target.value }))}
 className={inp} required autoComplete="new-password" />
 </div>
 <div className="pt-1">
 <button type="submit" disabled={savingPw} className={btnSecondary}>
 {savingPw ? t('profile.changingPassword') : t('profile.changePassword')}
 </button>
 </div>
 </form>
 </div>

 {/* Appearance */}
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-3">{t('profile.appearance')}</h3>
 <div className="space-y-3">
 <div>
 <label className={label}>{t('profile.language')}</label>
 <select
 value={localStorage.getItem('language') || 'en'}
 onChange={(e) => { localStorage.setItem('language', e.target.value); window.location.reload(); }}
 className={inp}>
 <option value="en">English</option>
 <option value="de">Deutsch</option>
 </select>
 </div>
 <div>
 <label className={label}>{t('profile.theme')}</label>
 <select
 value={localStorage.getItem('theme') || 'light'}
 onChange={(e) => { localStorage.setItem('theme', e.target.value); document.documentElement.setAttribute('data-theme', e.target.value); window.location.reload(); }}
 className={inp}>
 <option value="light">Light</option>
 <option value="dark">Dark</option>
 </select>
 </div>
 </div>
 </div>

 {/* #1422 Softphone (nicht für Portal-Accounts/JV-Partner) */}
 {!['portal_kunde', 'portal_mitarbeiter', 'jv_partner'].includes(user?.rolle) && (
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('profile.softphone') || 'Softphone'}</h3>
 <p className="text-[10px] text-[var(--text-secondary)] mb-3">{t('profile.softphoneDesc') || 'Telefonieren direkt im Browser (Headset empfohlen). Bei Aktivierung erscheint das Softphone oben rechts in der Leiste.'}</p>
 <div className="flex items-center justify-between">
 <span className="text-xs text-[var(--text-primary)]">{t('profile.softphoneEnable') || 'Softphone aktivieren'}</span>
 <ToggleSwitch checked={spEnabled} disabled={spSaving} onChange={async (v) => {
 setSpSaving(true);
 setSpEnabled(v);
 try {
 const res = await fetch(`${API}/api/twilio/voice/settings`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ enabled: v }),
 });
 if (!res.ok) throw new Error();
 await checkAuth(); // TopBar-Widget sofort ein-/ausblenden
 } catch {
 setSpEnabled(!v);
 } finally {
 setSpSaving(false);
 }
 }} />
 </div>

 {/* #1425 Durchstell-Kaskade: pro-MA-Konfig (nur sinnvoll mit Softphone, Handy geht auch ohne) */}
 <div className="mt-4 pt-4 border-t border-[var(--border-color,#E5E7EB)]">
 <div className="flex items-center justify-between mb-1">
 <span className="text-xs font-semibold text-[var(--text-primary)]">{t('profile.kaskade') || 'Durchstellen (Telefon-Hotline)'}</span>
 <ToggleSwitch checked={kk.aktiv} disabled={kkSaving} onChange={(v) => setKk(k => ({ ...k, aktiv: v }))} />
 </div>
 <p className="text-[10px] text-[var(--text-secondary)] mb-3">{t('profile.kaskadeDesc') || 'Flow stellt Anrufer an dich durch: erst Softphone, dann Handy, dann deine Vertretung. Ohne Erfolg nimmt Flow einen Fall mit Rückruf auf.'}</p>
 {kk.aktiv && (
 <div className="space-y-3">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className={label}>{t('profile.kaskadeSoftphoneTimeout') || 'Softphone klingelt (Sek.)'}</label>
 <input type="number" min="5" max="60" value={kk.softphone_timeout_sek}
 onChange={e => setKk(k => ({ ...k, softphone_timeout_sek: e.target.value }))} className={inp} />
 </div>
 <div>
 <label className={label}>{t('profile.kaskadeHandyTimeout') || 'Handy klingelt (Sek.)'}</label>
 <input type="number" min="5" max="60" value={kk.handy_timeout_sek}
 onChange={e => setKk(k => ({ ...k, handy_timeout_sek: e.target.value }))} className={inp} />
 </div>
 </div>
 <div>
 <label className={label}>{t('profile.kaskadeHandy') || 'Handy-Nummer (E.164, z.B. +43660…)'}</label>
 <div className="flex gap-2">
 <input type="tel" value={kk.handy_nummer || ''} placeholder="+43…"
 onChange={e => setKk(k => ({ ...k, handy_nummer: e.target.value }))} className={inp} />
 {kk.handy_vorschlag && kk.handy_nummer !== kk.handy_vorschlag && (
 <button type="button" onClick={() => setKk(k => ({ ...k, handy_nummer: k.handy_vorschlag }))}
 className="shrink-0 px-2.5 py-1.5 text-[11px] rounded-lg border border-[var(--border-color,#E5E7EB)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
 title={kk.handy_vorschlag}>
 {t('profile.kaskadeHandyVorschlag') || 'Aus Stammdaten'}
 </button>
 )}
 </div>
 </div>
 <div>
 <label className={label}>{t('profile.kaskadeVertretung') || 'Vertretung (optional, max. 1 Ebene)'}</label>
 <select value={kk.vertretung_user_id || ''} onChange={e => setKk(k => ({ ...k, vertretung_user_id: e.target.value || null }))} className={inp}>
 <option value="">{t('profile.kaskadeKeineVertretung') || 'Keine Vertretung'}</option>
 {kkTargets.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
 </select>
 </div>
 </div>
 )}
 {kkMsg && <div className="mt-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500">{kkMsg}</div>}
 {kkErr && <div className="mt-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">{kkErr}</div>}
 <div className="mt-3">
 <button type="button" disabled={kkSaving} className={btnSecondary} onClick={async () => {
 setKkSaving(true); setKkMsg(''); setKkErr('');
 try {
 const res = await fetch(`${API}/api/twilio/voice/settings`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ kaskade: {
 aktiv: !!kk.aktiv,
 softphone_timeout_sek: Number(kk.softphone_timeout_sek),
 handy_timeout_sek: Number(kk.handy_timeout_sek),
 handy_nummer: kk.handy_nummer || '',
 vertretung_user_id: kk.vertretung_user_id ? Number(kk.vertretung_user_id) : null,
 } }),
 });
 const d = await res.json().catch(() => ({}));
 if (!res.ok) throw new Error(d.error || 'Fehler');
 setKkMsg(t('profile.kaskadeSaved') || 'Kaskaden-Einstellungen gespeichert.');
 setTimeout(() => setKkMsg(''), 3000);
 } catch (e) {
 setKkErr(e.message);
 } finally {
 setKkSaving(false);
 }
 }}>
 {kkSaving ? (t('common.saving') || 'Speichern…') : (t('profile.kaskadeSave') || 'Kaskade speichern')}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 )}

 {/* ═══ SIGNATUR TAB ═══ */}
 {tab === 'signatur' && (
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('profile.mySignature')}</h3>
 <p className="text-[10px] text-[var(--text-secondary)] mb-4">{t('profile.signatureDesc')}</p>

 {sigLoading ? (
 <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : (
 <>
 {sigMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500 mb-3">{sigMsg}</div>}
 {sigErr && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400 mb-3">{sigErr}</div>}

 <div className="space-y-4">
 {/* Editor */}
 <div>
 <label className={label}>{t('profile.signatureEditor')}</label>
 <textarea
 value={sigHtml} onChange={(e) => setSigHtml(e.target.value)}
 rows={6}
 className={`${inp} font-mono text-xs resize-y`}
 placeholder={t('profile.signaturePlaceholder')}
 />
 </div>

 {/* Preview */}
 {sigHtml && (
 <div>
 <label className={label}>{t('profile.signaturePreview')}</label>
 <div
 className="p-4 rounded-xl border border-[var(--border-card)] bg-[var(--bg-inset)] text-sm text-[var(--text-primary)]"
 dangerouslySetInnerHTML={{ __html: sigHtml }}
 />
 </div>
 )}

 {/* Usage toggles */}
 <div>
 <label className={`${label} mb-2`}>{t('profile.useIn')}</label>
 <div className="space-y-2.5">
 {[
 { key: 'cases', label: t('profile.useCases'), value: sigUseCases, set: setSigUseCases },
 { key: 'sales', label: t('profile.useSales'), value: sigUseSales, set: setSigUseSales },
 { key: 'adress', label: t('profile.useAddress'), value: sigUseAdress, set: setSigUseAdress },
 ].map(toggle => (
 <label key={toggle.key} className="flex items-center gap-3 cursor-pointer">
 <input type="checkbox" checked={toggle.value} onChange={e => toggle.set(e.target.checked)}
 className="w-4 h-4 rounded accent-[#338edc]" />
 <span className="text-sm text-[var(--text-primary)]">{toggle.label}</span>
 </label>
 ))}
 </div>
 </div>

 <div className="pt-1">
 <button onClick={handleSignatureSave} disabled={sigSaving} className={btn}>
 {sigSaving ? t('common.saving') : t('profile.saveSignature')}
 </button>
 </div>
 </div>
 </>
 )}
 </div>
 )}

 {/* ═══ VERTRETUNGEN TAB ═══ */}
 {tab === 'vertretungen' && (
 <div className="space-y-4">
 <div className={card}>
 <div className="flex items-center justify-between mb-4">
 <div>
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('vertretung.meineVertretungen')}</h3>
 <p className="text-[10px] text-[var(--text-secondary)] mt-0.5">{t('vertretung.subtitle')}</p>
 </div>
 <button onClick={() => { setVtForm({ user_id: isAdmin ? '' : String(user.id), vertreter_id: '', permanent: true, gueltig_von: '', gueltig_bis: '', auto_vertretung: false }); setVtModal(true); setVtMsg(''); setVtErr(''); }}
 className={btn}>
 {t('vertretung.add')}
 </button>
 </div>

 <div className="bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2.5 mb-3 text-[11px] leading-relaxed text-[var(--text-secondary)]">
 {t('vertretung.autoHint')}
 </div>

 {vtMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500 mb-3">{vtMsg}</div>}
 {vtErr && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400 mb-3">{vtErr}</div>}

 {vtLoading ? (
 <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : vtList.length === 0 ? (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-xs">{t('vertretung.keineVertretungen')}</div>
 ) : (
 <div className="border border-[var(--border-card)] rounded-xl overflow-hidden">
 <table className="w-full text-sm">
 <thead>
 <tr className="border-b border-[var(--border-card)] bg-[var(--bg-inset)]">
 <th className="text-left px-3 py-2 text-[10px] font-bold text-[var(--text-tertiary)] uppercase">{t('vertretung.vertretungFuer')}</th>
 <th className="text-left px-3 py-2 text-[10px] font-bold text-[var(--text-tertiary)] uppercase">{t('vertretung.vertreter')}</th>
 <th className="text-left px-3 py-2 text-[10px] font-bold text-[var(--text-tertiary)] uppercase">{t('vertretung.zeitraum')}</th>
 <th className="text-center px-3 py-2 text-[10px] font-bold text-[var(--text-tertiary)] uppercase">{t('vertretung.auto')}</th>
 <th className="w-10"></th>
 </tr>
 </thead>
 <tbody>
 {vtList.map(v => (
 <tr key={v.id} className="border-b border-[var(--border-card)]/50">
 <td className="px-3 py-2.5 text-xs text-[var(--text-primary)]">{v.user_vorname} {v.user_nachname}</td>
 <td className="px-3 py-2.5 text-xs text-[var(--text-primary)]">{v.vertreter_vorname} {v.vertreter_nachname}</td>
 <td className="px-3 py-2.5 text-xs text-[var(--text-secondary)]">
 {v.permanent ? t('vertretung.permanent') : (
 <span>{v.gueltig_von ? new Date(v.gueltig_von).toLocaleDateString('de-AT') : '–'} — {v.gueltig_bis ? new Date(v.gueltig_bis).toLocaleDateString('de-AT') : '–'}</span>
 )}
 </td>
 <td className="px-3 py-2.5">
 <div className="flex justify-center"><ToggleSwitch size="sm" checked={!!v.auto_vertretung} onChange={val => handleVtToggleAuto(v, val)} /></div>
 </td>
 <td className="px-3 py-2.5">
 <button onClick={() => handleVtDelete(v.id)} className="text-red-400 hover:text-red-300 transition-colors" title={t('vertretung.remove')}>
 <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>

 {/* Add Modal */}
 {vtModal && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setVtModal(false)}>
 <div className={`${card} shadow-sm w-full max-w-md`} onClick={e => e.stopPropagation()}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4">{t('vertretung.add')}</h3>
 <form onSubmit={handleVtSave} className="space-y-3">
 {isAdmin && (
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase mb-1">{t('vertretung.vertretungFuer')}</label>
 <select value={vtForm.user_id} onChange={e => setVtForm(f => ({ ...f, user_id: e.target.value }))} className={inp}>
 <option value="">{t('vertretung.selectUser')}</option>
 {vtUsers.filter(u => u.id !== user.id).map(u => (
 <option key={u.id} value={u.id}>{u.vorname} {u.nachname}</option>
 ))}
 </select>
 </div>
 )}
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase mb-1">{t('vertretung.vertreter')}</label>
 <select value={vtForm.vertreter_id} onChange={e => setVtForm(f => ({ ...f, vertreter_id: e.target.value }))} className={inp}>
 <option value="">{t('vertretung.selectUser')}</option>
 {vtUsers.filter(u => u.id !== Number(vtForm.user_id || user.id)).map(u => (
 <option key={u.id} value={u.id}>{u.vorname} {u.nachname}</option>
 ))}
 </select>
 </div>
 <div>
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase mb-2">{t('vertretung.art')}</label>
 <div className="space-y-2">
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="radio" checked={vtForm.permanent} onChange={() => setVtForm(f => ({ ...f, permanent: true }))} className="accent-[#338edc]" />
 <span className="text-xs text-[var(--text-primary)]">{t('vertretung.permanent')}</span>
 </label>
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="radio" checked={!vtForm.permanent} onChange={() => setVtForm(f => ({ ...f, permanent: false }))} className="accent-[#338edc]" />
 <span className="text-xs text-[var(--text-primary)]">{t('vertretung.zeitraum')}</span>
 </label>
 </div>
 </div>
 {!vtForm.permanent && (
 <div className="flex items-center gap-2">
 <div className="flex-1">
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase mb-1">{t('vertretung.gueltigVon')}</label>
 <input type="date" value={vtForm.gueltig_von} onChange={e => setVtForm(f => ({ ...f, gueltig_von: e.target.value }))} className={inp} />
 </div>
 <span className="text-[var(--text-tertiary)] pt-4">—</span>
 <div className="flex-1">
 <label className="block text-[10px] font-semibold text-[var(--text-secondary)] uppercase mb-1">{t('vertretung.gueltigBis')}</label>
 <input type="date" value={vtForm.gueltig_bis} onChange={e => setVtForm(f => ({ ...f, gueltig_bis: e.target.value }))} className={inp} />
 </div>
 </div>
 )}
 <div className="border-t border-[var(--border-card)] pt-3">
 <label className="flex items-center justify-between gap-3 cursor-pointer">
 <span className="text-xs font-medium text-[var(--text-primary)]">{t('vertretung.autoVertretung')}</span>
 <ToggleSwitch size="sm" checked={!!vtForm.auto_vertretung} onChange={val => setVtForm(f => ({ ...f, auto_vertretung: val }))} />
 </label>
 <p className="text-[10px] text-[var(--text-tertiary)] mt-1.5 leading-relaxed">{t('vertretung.autoHint')}</p>
 </div>
 {vtErr && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400">{vtErr}</div>}
 <div className="flex items-center gap-3 pt-2">
 <button type="button" onClick={() => setVtModal(false)} className={btnSecondary}>{t('common.cancel')}</button>
 <button type="submit" disabled={vtSaving || !vtForm.vertreter_id} className={btn}>
 {vtSaving ? t('common.saving') : t('common.save')}
 </button>
 </div>
 </form>
 </div>
 </div>
 )}
 </div>
 )}

 {/* ═══ BENACHRICHTIGUNGEN TAB ═══ */}
 {tab === 'benachrichtigungen' && (
 <div className="space-y-6">
 <div className={card}>
 <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
 <div>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('profile.notificationSettings')}</h3>
 <p className="text-[10px] text-[var(--text-secondary)]">{t('profile.notificationDesc')}</p>
 </div>
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)] flex">
 <button
 onClick={() => setNotifView('kategorie')}
 className={`px-3 py-1.5 rounded-[8px] text-[11px] font-medium transition-colors ${notifView === 'kategorie' ? 'bg-white text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'}`}
 >{t('notificationsPrefs.viewCategory')}</button>
 <button
 onClick={() => setNotifView('event')}
 className={`px-3 py-1.5 rounded-[8px] text-[11px] font-medium transition-colors ${notifView === 'event' ? 'bg-white text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-secondary)]'}`}
 >{t('notificationsPrefs.viewEvent')}</button>
 </div>
 </div>

 {notifView === 'kategorie' && (prefsLoading ? (
 <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : (
 <div className="space-y-0">
 <div className="grid grid-cols-[1fr,60px,60px,100px] gap-2 pb-2 border-b border-[var(--border-card)]">
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('profile.category')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.inApp')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.email')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.digest')}</span>
 </div>
 {prefs.map(p => (
 <div key={p.kategorie} className={`grid grid-cols-[1fr,60px,60px,100px] gap-2 py-3 border-b border-[var(--border-card)]/50 items-center ${prefsSaving === p.kategorie ? 'opacity-50' : ''}`}>
 <span className="text-xs text-[var(--text-primary)] font-medium">{p.label}</span>
 <div className="flex justify-center"><ToggleSwitch size="sm" checked={p.in_app} onChange={v => updatePref(p.kategorie, 'in_app', v)} /></div>
 <div className="flex justify-center"><ToggleSwitch size="sm" checked={p.email} onChange={v => updatePref(p.kategorie, 'email', v)} /></div>
 <div className="flex justify-center">
 {p.email ? (
 <select value={p.digest} onChange={e => updatePref(p.kategorie, 'digest', e.target.value)}
 className="bg-[var(--bg-inset)] border border-[var(--border-input)] rounded px-1.5 py-0.5 text-[10px] text-[var(--text-secondary)] focus:outline-none">
 <option value="sofort">{t('profile.immediately')}</option>
 <option value="taeglich">{t('profile.daily')}</option>
 <option value="woechentlich">{t('profile.weekly')}</option>
 </select>
 ) : (
 <span className="text-[10px] text-[var(--text-tertiary)]">—</span>
 )}
 </div>
 </div>
 ))}
 </div>
 ))}

 {notifView === 'event' && (eventsLoading ? (
 <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : (
 <div>
 <div className="flex items-center gap-2 mb-3">
 <input
 type="text"
 value={eventFilter}
 onChange={e => setEventFilter(e.target.value)}
 placeholder={t('notificationsPrefs.filterPlaceholder')}
 className="flex-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-[10px] px-3 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc]"
 />
 <span className="text-[10px] text-[var(--text-tertiary)]">{events.length} {t('notificationsPrefs.eventsTotal')}</span>
 </div>
 <div className="grid grid-cols-[1.6fr,56px,56px,56px,72px,72px,28px] gap-2 pb-2 border-b border-[var(--border-card)]">
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{t('notificationsPrefs.event')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.inApp')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.email')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.sms')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.telegram')}</span>
 <span className="text-[10px] font-semibold text-[var(--text-secondary)] uppercase tracking-wider text-center">{t('profile.notifPrefs.digest')}</span>
 <span />
 </div>
 <div className="max-h-[560px] overflow-y-auto">
 {events
 .filter(ev => !eventFilter || ev.event_key.toLowerCase().includes(eventFilter.toLowerCase()) || (ev.label_de || '').toLowerCase().includes(eventFilter.toLowerCase()) || (ev.label_en || '').toLowerCase().includes(eventFilter.toLowerCase()))
 .map(ev => {
 const isCustom = !!ev.user_pref;
 const isCritical = ev.kritisch;
 return (
 <div key={ev.event_key} className={`grid grid-cols-[1.6fr,56px,56px,56px,72px,72px,28px] gap-2 py-2.5 border-b border-[var(--border-card)]/50 items-center ${eventSaving === ev.event_key ? 'opacity-50' : ''}`}>
 <div className="min-w-0">
 <div className="flex items-center gap-1.5">
 <span className="text-[11px] text-[var(--text-primary)] font-medium truncate">{language === 'en' ? (ev.label_en || ev.label_de) : ev.label_de}</span>
 {isCritical && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 font-semibold">{t('notificationsPrefs.critical')}</span>}
 {isCustom && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500 font-semibold">{t('notificationsPrefs.customized')}</span>}
 </div>
 <div className="text-[10px] text-[var(--text-tertiary)] font-mono truncate">{ev.event_key}</div>
 </div>
 <div className="flex justify-center"><ToggleSwitch size="sm" checked={ev.effective.in_app} onChange={v => updateEventPref(ev.event_key, 'in_app', v)} disabled={isCritical && ev.effective.in_app && !isCustom} /></div>
 <div className="flex justify-center"><ToggleSwitch size="sm" checked={ev.effective.email} onChange={v => updateEventPref(ev.event_key, 'email', v)} /></div>
 <div className="flex justify-center"><ToggleSwitch size="sm" checked={ev.effective.sms} onChange={v => updateEventPref(ev.event_key, 'sms', v)} /></div>
 <div className="flex justify-center"><ToggleSwitch size="sm" checked={ev.effective.telegram} onChange={v => updateEventPref(ev.event_key, 'telegram', v)} /></div>
 <div className="flex justify-center">
 <select value={ev.effective.digest || 'sofort'} onChange={e => updateEventPref(ev.event_key, 'digest', e.target.value)}
 className="bg-[var(--bg-inset)] border border-[var(--border-input)] rounded px-1 py-0.5 text-[10px] text-[var(--text-secondary)] focus:outline-none">
 <option value="sofort">{t('profile.immediately')}</option>
 <option value="taeglich">{t('profile.daily')}</option>
 <option value="woechentlich">{t('profile.weekly')}</option>
 </select>
 </div>
 <div className="flex justify-center">
 {isCustom && (
 <button onClick={() => resetEventPref(ev.event_key)} title={t('notificationsPrefs.resetTooltip')}
 className="text-[var(--text-tertiary)] hover:text-[#338edc] transition-colors text-[14px] leading-none">↺</button>
 )}
 </div>
 </div>
 );
 })}
 </div>
 </div>
 ))}
 </div>

 {/* #1049 Quiet-Hours Card */}
 <div className={card}>
 <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
 <div>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('profile.quietHours.title')}</h3>
 <p className="text-[10px] text-[var(--text-secondary)]">{t('profile.quietHours.subtitle')}</p>
 </div>
 <ToggleSwitch checked={!!qh.enabled} onChange={v => setQh(prev => ({ ...prev, enabled: v }))} disabled={qhLoading} />
 </div>
 {qh.enabled && (
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] block mb-1">{t('profile.quietHours.from')}</label>
 <input type="time" value={qh.start_time} onChange={e => setQh(prev => ({ ...prev, start_time: e.target.value }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm" />
 </div>
 <div>
 <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] block mb-1">{t('profile.quietHours.to')}</label>
 <input type="time" value={qh.end_time} onChange={e => setQh(prev => ({ ...prev, end_time: e.target.value }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm" />
 </div>
 </div>
 <div>
 <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] block mb-2">{t('profile.quietHours.channels')}</label>
 <div className="flex flex-wrap gap-2">
 {['sms','telegram','email','in_app'].map(ch => (
 <button key={ch} onClick={() => toggleQhChannel(ch)}
 className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${qh.channels.includes(ch)
 ? 'bg-[var(--primary,#338edc)] text-white border-[var(--primary,#338edc)]'
 : 'bg-[var(--bg-inset)] text-[var(--text-secondary)] border-[var(--border-card)] hover:border-[var(--primary,#338edc)]'}`}>
 {t(`channels.${ch}`)}
 </button>
 ))}
 </div>
 </div>
 <div className="flex items-center gap-2">
 <ToggleSwitch size="sm" checked={!!qh.weekend_only} onChange={v => setQh(prev => ({ ...prev, weekend_only: v }))} />
 <span className="text-xs text-[var(--text-secondary)]">{t('profile.quietHours.weekendOnly')}</span>
 </div>
 <p className="text-[10px] text-[var(--text-tertiary)] italic">{t('profile.quietHours.bypassNote')}</p>
 </div>
 )}
 <div className="flex items-center gap-3 mt-4">
 <button onClick={saveQuietHours} disabled={qhSaving || qhLoading}
 className="px-4 py-2 rounded-lg bg-[var(--primary,#338edc)] text-white text-sm font-medium hover:bg-[var(--primary,#338edc)]/90 disabled:opacity-50">
 {qhSaving ? t('common.saving') : t('common.save')}
 </button>
 {qhMsg && <span className="text-xs text-emerald-600">{qhMsg}</span>}
 </div>
 </div>
 </div>
 )}

 {/* ═══ OMS TAB ═══ */}
 {tab === 'oms' && (
 <div className="space-y-6">
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('omsSettings.title')}</h3>
 <p className="text-[10px] text-[var(--text-secondary)] mb-5">{t('omsSettings.subtitle')}</p>

 {omsLoading ? (
 <div className="flex justify-center py-8"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : (
 <>
 <div className="flex items-center gap-2 mb-5 px-3 py-2 bg-[var(--bg-inset)] rounded-xl border border-[var(--border-card)]">
 <span className={`w-2 h-2 rounded-full flex-shrink-0 ${omsStatus.color}`} />
 <span className="text-xs text-[var(--text-primary)]">{omsStatus.text}</span>
 {omsSettings?.oms_last_verified && omsSettings.oms_connection_verified && (
 <span className="text-[10px] text-[var(--text-secondary)] ml-auto">
 {t('omsSettings.lastVerified')}: {new Date(omsSettings.oms_last_verified).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
 </span>
 )}
 </div>

 <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/20 mb-4">
 <span className="text-primary-500 mt-0.5 flex-shrink-0 text-xs">&#9432;</span>
 <p className="text-[10px] text-primary-700 dark:text-primary-300 leading-relaxed">{t('omsSettings.systemVsPersonalHint')}</p>
 </div>

 {omsMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500 mb-4">{omsMsg}</div>}
 {omsErr && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400 mb-4">{omsErr}</div>}

 <form onSubmit={handleOmsSave} className="space-y-3">
 <div>
 <label className={label}>{t('omsSettings.username')}</label>
 <input type="text" value={omsForm.oms_username} onChange={e => setOmsForm(f => ({ ...f, oms_username: e.target.value }))}
 className={inp} placeholder={t('omsSettings.usernamePlaceholder')} autoComplete="username" />
 </div>
 <div>
 <label className={label}>{t('omsSettings.password')}</label>
 <input type="password" value={omsForm.oms_password} onChange={e => setOmsForm(f => ({ ...f, oms_password: e.target.value }))}
 className={inp} placeholder={t('omsSettings.passwordPlaceholder')} autoComplete="off" />
 </div>
 <div className="flex items-center gap-3 pt-2">
 <button type="submit" disabled={omsSaving} className={btn}>
 {omsSaving ? t('omsSettings.saving') : t('omsSettings.save')}
 </button>
 {omsSettings?.oms_username && (
 <button type="button" onClick={handleOmsTest} disabled={omsTesting} className={btnSecondary}>
 {omsTesting ? t('omsSettings.testing') : t('omsSettings.testConnection')}
 </button>
 )}
 </div>
 </form>
 </>
 )}
 </div>

 {/* OMS-User Zuordnung (Admin only) */}
 {isAdmin && (
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('omsSettings.userMapping')}</h3>
 <p className="text-[10px] text-[var(--text-secondary)] mb-4">{t('omsSettings.userMappingDesc')}</p>
 {omsUsersLoading ? (
 <div className="flex justify-center py-4"><div className="animate-spin h-4 w-4 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 ) : omsUsers.length > 0 ? (
 <select
 value={omsSettings?.oms_user_id || ''}
 onChange={e => handleOmsUserSelect(e.target.value)}
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-input)] rounded-xl px-3 py-2.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]/50 focus:ring-1 focus:ring-[#338edc]/30"
 >
 <option value="">{t('omsSettings.selectOmsUser')}</option>
 {omsUsers.map(ou => (
 <option key={ou.id} value={ou.id}>{ou.name} ({ou.login})</option>
 ))}
 </select>
 ) : (
 <div className="text-xs text-[var(--text-tertiary)] px-3 py-2 bg-[var(--bg-inset)] rounded-xl border border-[var(--border-card)]">
 {t('omsSettings.noOmsUsers')}
 </div>
 )}
 </div>
 )}

 {/* OMS Sync */}
 {omsSettings?.oms_connection_verified && (
 <div className={card}>
 <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">{t('profile.omsSyncTitle')}</h3>
 <p className="text-[10px] text-[var(--text-secondary)] mb-4">{t('profile.omsSyncDesc')}</p>

 {syncMsg && <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 text-xs text-emerald-500 mb-4">{syncMsg}</div>}
 {syncErr && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-xs text-red-400 mb-4">{syncErr}</div>}

 <div className="space-y-2 mb-4">
 <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-inset)] rounded-xl border border-[var(--border-card)]">
 <div className="flex items-center gap-2">
 <svg className="w-4 h-4 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
 <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0" />
 </svg>
 <span className="text-xs text-[var(--text-primary)]">{t('profile.avatarLabel')}</span>
 </div>
 <span className={`text-[10px] font-medium ${syncStatus?.avatar_synced_to_oms ? 'text-emerald-500' : 'text-[var(--text-tertiary)]'}`}>
 {syncStatus?.avatar_synced_to_oms ? t('profile.synced') : t('profile.notSynced')}
 </span>
 </div>
 <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-inset)] rounded-xl border border-[var(--border-card)]">
 <div className="flex items-center gap-2">
 <svg className="w-4 h-4 text-[var(--text-secondary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
 <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
 </svg>
 <span className="text-xs text-[var(--text-primary)]">{t('profile.signatureLabel')}</span>
 </div>
 <span className={`text-[10px] font-medium ${syncStatus?.signature_synced_to_oms ? 'text-emerald-500' : 'text-[var(--text-tertiary)]'}`}>
 {syncStatus?.signature_synced_to_oms ? t('profile.synced') : t('profile.notSynced')}
 </span>
 </div>
 </div>

 <button onClick={handleOmsSync} disabled={syncing} className={`${btn} flex items-center gap-2`}>
 {syncing ? (
 <>
 <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
 {t('profile.omsSyncing')}
 </>
 ) : (
 <>
 <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
 <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M21.015 4.356v4.992" />
 </svg>
 {t('profile.omsSyncNow')}
 </>
 )}
 </button>
 </div>
 )}
 </div>
 )}
 </div>
 );
}
